import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap, switchMap, map, take, catchError, timeout } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { TransferState, makeStateKey } from '@angular/platform-browser';
import { ApiCallService } from '../../services/api-call.service';
import * as ApiCacheActions from './api-cache.actions';
import { selectApiCacheEntry } from './api-cache.selectors';
import { ApiCacheRootState } from './api-cache.reducer';

/**
 * Cache types for API requests
 */
export const CacheTypes = {
  ALWAYS_FRESH: 'always_fresh', // No caching, always fetches from server
  REFRESH_EVERY_N: 'refresh_every_n', // Caches data, refreshes every Nth request
  PRERENDERED: 'prerendered' // SSR only - no browser API calls
} as const;

export type CacheType = typeof CacheTypes[keyof typeof CacheTypes];

@Injectable({
  providedIn: 'root'
})
export class ApiCacheService {
  constructor(
    private readonly apiCallService: ApiCallService,
    private readonly store: Store<{ apiCache: ApiCacheRootState }>,
    private readonly transferState: TransferState
  ) { }

  /**
   * Get data from API with caching strategy
   * @param endpoint API endpoint
   * @param cacheType Type of caching strategy
   * @param refreshInterval Refresh interval for REFRESH_EVERY_N cache type (default: 2)
   * @param options HTTP request options (default: {})
   * @param responseType Response type (optional)
   * @returns Observable with API response
   */
  get(
    endpoint: string,
    cacheType: CacheType = CacheTypes.ALWAYS_FRESH,
    refreshInterval: number = 2,
    options: any = {},
    responseType?: any
  ): Observable<any> {
    switch (cacheType) {
      case CacheTypes.ALWAYS_FRESH:
        // Always fetch from server, no caching
        return this.apiCallService.get(endpoint, false, options, responseType);

      case CacheTypes.REFRESH_EVERY_N: {
        // Cache with refresh on Nth request
        const cacheKey = this.getCacheKey(endpoint);

        // Check if cached data exists
        return this.store.select(selectApiCacheEntry(cacheKey)).pipe(
          take(1),
          switchMap((cachedEntry) => {
            if (cachedEntry) {
              // Check if we need to refresh (counter >= refreshInterval)
              if (cachedEntry.requestCount >= refreshInterval) {
                // Trigger background refresh (fire-and-forget)
                this.backgroundRefresh(endpoint, cacheKey, refreshInterval, options, responseType);
                // Return cached data immediately
                return of(cachedEntry.data);
              }

              // Return cached data and increment counter
              this.store.dispatch(ApiCacheActions.incrementRequestCount({ key: cacheKey }));
              return of(cachedEntry.data);
            }

            // No cache exists, fetch fresh data
            return this.fetchAndCache(endpoint, cacheKey, refreshInterval, options, responseType);
          })
        );
      }

      case CacheTypes.PRERENDERED: {
        // SSR only - no browser API calls
        const cacheKey = this.getCacheKey(endpoint);
        const stateKey = makeStateKey<any>(cacheKey);

        // Return from TransferState if available
        if (this.transferState.hasKey(stateKey)) {
          const data = this.transferState.get(stateKey, null);
          this.transferState.remove(stateKey);
          return of(data);
        }

        // Server: Fetch and store in TransferState (timeout prevents prerender hang)
        return this.apiCallService.get(endpoint, false, options, responseType).pipe(
          timeout(10000),
          tap((data) => this.transferState.set(stateKey, data)),
          catchError(() => of(null))
        );
      }

      default:
        // Fallback: treat as ALWAYS_FRESH
        return this.apiCallService.get(endpoint, false, options, responseType);
    }
  }

  /**
   * Fetch data from API and cache it
   * @param endpoint API endpoint
   * @param cacheKey Cache key
   * @param refreshInterval Refresh interval for this cache entry
   * @param options HTTP request options
   * @param responseType Response type (optional)
   * @returns Observable with API response
   */
  private fetchAndCache(
    endpoint: string,
    cacheKey: string,
    refreshInterval: number,
    options: any = {},
    responseType?: any
  ): Observable<any> {
    return this.apiCallService.get(endpoint, false, options, responseType).pipe(
      tap((data: any) => {
        this.store.dispatch(
          ApiCacheActions.setApiCacheData({
            key: cacheKey,
            data: {
              data,
              requestCount: 1,
              refreshInterval
            }
          })
        );
      })
    );
  }

  /**
   * Refresh cache in the background without blocking
   * Fetches fresh data and updates cache for next request
   * @param endpoint API endpoint
   * @param cacheKey Cache key
   * @param refreshInterval Refresh interval for this cache entry
   * @param options HTTP request options
   * @param responseType Response type (optional)
   */
  private backgroundRefresh(
    endpoint: string,
    cacheKey: string,
    refreshInterval: number,
    options: any = {},
    responseType?: any
  ): void {
    this.apiCallService.get(endpoint, false, options, responseType).pipe(
      take(1)
    ).subscribe((data: any) => {
      this.store.dispatch(
        ApiCacheActions.setApiCacheData({
          key: cacheKey,
          data: {
            data,
            requestCount: 1,
            refreshInterval
          }
        })
      );
    });
  }

  /**
   * Generate cache key from endpoint
   * @param endpoint API endpoint
   * @returns Cache key
   */
  private getCacheKey(endpoint: string): string {
    return endpoint;
  }

  /**
   * Clear cache for a specific endpoint. Next call will fetch fresh data.
   * @param endpoint API endpoint
   */
  clearCache(endpoint: string): void {
    const cacheKey = this.getCacheKey(endpoint);
    this.store.dispatch(ApiCacheActions.clearApiCacheData({ key: cacheKey }));
  }

  /**
   * Clear all cached data
   */
  clearAllCache(): void {
    this.store.dispatch(ApiCacheActions.clearAllApiCache());
  }

  /**
   * Get current request count for an endpoint
   * @param endpoint API endpoint
   * @returns Observable with request count or null if not cached
   */
  getRequestCount(endpoint: string): Observable<number | null> {
    const cacheKey = this.getCacheKey(endpoint);
    return this.store.select(selectApiCacheEntry(cacheKey)).pipe(
      take(1),
      map((entry) => (entry ? entry.requestCount : null))
    );
  }

  /**
   * Check if endpoint has cached data
   * @param endpoint API endpoint
   * @returns Observable with true if cached data exists
   */
  hasCache(endpoint: string): Observable<boolean> {
    const cacheKey = this.getCacheKey(endpoint);
    return this.store.select(selectApiCacheEntry(cacheKey)).pipe(
      take(1),
      map((entry) => !!entry)
    );
  }
}